import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  SectionHeader,
  Spinner,
  Stamp,
  Stat,
  Tabs,
  Textarea,
} from "../ui";
import { LABELS_16, MOCK_EXTRACTIONS } from "../data/mock";
import { downloadExportDraft } from "../api/monograph";
import { useBuildMonographEn } from "../hooks/useBuildMonographEn";
import {
  useBulkUpdateExtractions,
  useExtractions,
  useExtractionsSummary,
  usePatchExtraction,
} from "../hooks/useExtractions";
import { useJobPolling } from "../hooks/useJobPolling";
import { useMonograph } from "../hooks/useMonograph";
import type { ExportDraftFormat, Extraction, ExtractionStatus, Project } from "../types";

type Filter = "all" | ExtractionStatus;
type BulkAction = "approved" | "rejected";

export interface Step6ReviewENProps {
  project: Project;
  mock: boolean;
  onAdvance: (n: number) => void;
}

interface BulkConfirm {
  action: BulkAction;
  ids: string[];
  context: string;
}

function statusStamp(status: ExtractionStatus) {
  if (status === "approved") return <Stamp tone="ok">Aprovado</Stamp>;
  if (status === "rejected") return <Stamp tone="strike">Rejeitado</Stamp>;
  return <Stamp tone="ink">Pendente</Stamp>;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function BulkConfirmDialog({
  bulk,
  isPending,
  onCancel,
  onConfirm,
}: {
  bulk: BulkConfirm | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!bulk) return null;
  const isReject = bulk.action === "rejected";
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 anim-fade flex items-start justify-center pt-24 px-6"
      style={{ background: "rgba(10,10,10,0.4)" }}
      onClick={onCancel}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="bg-[var(--paper)] border border-[var(--ink)] w-full max-w-lg page-edge anim-rise"
      >
        <div className="px-8 py-5 border-b-2 border-[var(--ink)]">
          <div className="eyebrow">§ Revisao em lote</div>
          <div className="display-4 mt-1">
            {isReject ? "Rejeitar visiveis" : "Aprovar visiveis"}
          </div>
        </div>
        <div className="px-8 py-6 space-y-3">
          <p className="body">
            {isReject ? "Rejeitar" : "Aprovar"} todos os {bulk.ids.length} trechos
            visiveis?
          </p>
          <p className="caption">{bulk.context}</p>
        </div>
        <div className="px-8 py-4 border-t border-[var(--rule)] flex items-center justify-end gap-3">
          <Button variant="line" onClick={onCancel} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="ink"
            icon={isPending ? Icon.Loader : isReject ? Icon.X : Icon.Check}
            onClick={onConfirm}
            disabled={isPending}
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Step6ReviewEN({ project, mock, onAdvance }: Step6ReviewENProps) {
  const qc = useQueryClient();
  const extractionsQuery = useExtractions(project.id, undefined, !mock);
  const summaryQuery = useExtractionsSummary(mock ? undefined : project.id);
  const monographQuery = useMonograph(mock ? undefined : project.id);
  const patchExtraction = usePatchExtraction(project.id);
  const bulkUpdate = useBulkUpdateExtractions(project.id);
  const build = useBuildMonographEn(project.id);
  const [buildJobId, setBuildJobId] = useState<string | null>(null);
  const buildJob = useJobPolling(buildJobId);
  const [filter, setFilter] = useState<Filter>("pending");
  const [labelFilter, setLabelFilter] = useState<string>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState<BulkConfirm | null>(null);
  const [reviewDirty, setReviewDirty] = useState(false);
  const [latestBuildSucceeded, setLatestBuildSucceeded] = useState(false);
  const [exportPending, setExportPending] = useState<ExportDraftFormat | "html-open" | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const extractions = mock ? MOCK_EXTRACTIONS : (extractionsQuery.data ?? []);
  const monographSections = monographQuery.data ?? [];
  const counts = useMemo(() => {
    const fromSummary = summaryQuery.data;
    if (fromSummary?.length) {
      return fromSummary.reduce(
        (acc, row) => ({
          all: acc.all + row.total,
          approved: acc.approved + row.approved,
          pending: acc.pending + row.pending,
          rejected: acc.rejected + row.rejected,
        }),
        { all: 0, approved: 0, pending: 0, rejected: 0 },
      );
    }
    return {
      all: extractions.length,
      approved: extractions.filter((e) => e.status === "approved").length,
      pending: extractions.filter((e) => e.status === "pending").length,
      rejected: extractions.filter((e) => e.status === "rejected").length,
    };
  }, [extractions, summaryQuery.data]);
  const visible = extractions.filter((e) => {
    const statusMatch = filter === "all" || e.status === filter;
    const labelMatch = labelFilter === "all" || e.label === labelFilter;
    return statusMatch && labelMatch;
  });
  const isLoading = !mock && (extractionsQuery.isPending || summaryQuery.isPending);
  const hasError = !mock && (extractionsQuery.isError || summaryQuery.isError);
  const hasBuiltSections = monographSections.some((section) =>
    Boolean(section.final_text_en?.trim()),
  );
  const buildReady = (latestBuildSucceeded || hasBuiltSections) && !reviewDirty;
  const handoffReady = counts.approved > 0 && buildReady;
  const bulkPending = bulkUpdate.isPending;

  useEffect(() => {
    if (buildJob.data?.status === "success") {
      setLatestBuildSucceeded(true);
      setReviewDirty(false);
      qc.invalidateQueries({ queryKey: ["monograph", project.id] });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["extractions", project.id] });
      qc.invalidateQueries({ queryKey: ["extractions-summary", project.id] });
    }
  }, [buildJob.data?.status, project.id, qc]);

  const markDirtyAfterReview = () => {
    setReviewDirty(true);
    setLatestBuildSucceeded(false);
  };

  const patchStatus = async (id: string, status: ExtractionStatus) => {
    await patchExtraction.mutateAsync({ id, body: { status } });
    markDirtyAfterReview();
  };

  const startEdit = (extraction: Extraction) => {
    setEditingId(extraction.id);
    setEditText(extraction.text_edited_en || extraction.text_verbatim_en);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    await patchExtraction.mutateAsync({
      id: editingId,
      body: { text_edited_en: editText, status: "approved" },
    });
    setEditingId(null);
    markDirtyAfterReview();
  };

  const openBulk = (action: BulkAction) => {
    const statusText = filter === "all" ? "todos os status" : filter;
    const labelText =
      labelFilter === "all"
        ? "todas as secoes"
        : LABELS_16.find((label) => label.key === labelFilter)?.title_en || labelFilter;
    setBulkConfirm({
      action,
      ids: visible.map((extraction) => extraction.id),
      context: `Filtro atual: ${statusText}; ${labelText}.`,
    });
  };

  const confirmBulk = async () => {
    if (!bulkConfirm) return;
    await bulkUpdate.mutateAsync({
      filter: {
        status: filter === "all" ? undefined : filter,
        label: labelFilter === "all" ? undefined : labelFilter,
      },
      set: { status: bulkConfirm.action },
      ids: bulkConfirm.ids,
    });
    setBulkConfirm(null);
    markDirtyAfterReview();
  };

  const startBuild = async () => {
    const response = await build.mutateAsync();
    setBuildJobId(response.job_id);
  };

  const exportDraft = async (format: ExportDraftFormat, mode: "download" | "open") => {
    setExportPending(mode === "open" ? "html-open" : format);
    try {
      const blob = await downloadExportDraft(project.id, format);
      if (mode === "open") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      } else {
        const ext = format === "docx" ? "docx" : "txt";
        downloadBlob(blob, `${project.drug_api_name}-draft-en.${ext}`);
      }
    } finally {
      setExportPending(null);
    }
  };

  const copyMonographText = async () => {
    const text = monographSections
      .filter((section) => section.final_text_en?.trim())
      .map((section) => `${section.title_en || section.label}\n\n${section.final_text_en}`)
      .join("\n\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="anim-fade">
      <SectionHeader
        number={6}
        eyebrow="Etapa 06 · Aprovacao"
        title="Revisao em ingles"
        lede="Cada trecho deve ser aprovado, editado ou rejeitado antes da traducao. O draft EN e gerado a partir dos trechos aprovados."
        action={
          <Button
            variant="ink"
            iconRight={Icon.ArrowRight}
            disabled={!handoffReady}
            onClick={() => onAdvance(7)}
          >
            Avancar para traducao
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-y-2 border-[var(--ink)] mb-8">
        <div className="px-6 py-5 border-r border-[var(--rule)]"><Stat value={counts.all} label="Total" size="md" /></div>
        <div className="px-6 py-5 lg:border-r border-[var(--rule)]"><Stat value={counts.approved} label="Aprovados" size="md" /></div>
        <div className="px-6 py-5 border-t lg:border-t-0 border-r border-[var(--rule)]"><Stat value={counts.pending} label="Pendentes" size="md" /></div>
        <div className="px-6 py-5 border-t lg:border-t-0 border-[var(--rule)]"><Stat value={counts.rejected} label="Rejeitados" size="md" /></div>
      </div>

      <div className="border-b border-[var(--rule)] pb-6 mb-8 space-y-5">
        <div className="flex flex-wrap items-end gap-5 justify-between">
          <Tabs<Filter>
            value={filter}
            onChange={setFilter}
            tabs={[
              { value: "pending", label: "Pendentes", count: counts.pending },
              { value: "approved", label: "Aprovados", count: counts.approved },
              { value: "rejected", label: "Rejeitados", count: counts.rejected },
              { value: "all", label: "Todos", count: counts.all },
            ]}
          />
          <label className="caption flex items-center gap-2">
            Secao
            <select
              value={labelFilter}
              onChange={(event) => setLabelFilter(event.target.value)}
              className="bg-transparent border border-[var(--rule)] px-3 py-2 mono text-[12px]"
            >
              <option value="all">Todas</option>
              {LABELS_16.map((label) => (
                <option key={label.key} value={label.key}>
                  {label.title_en}
                </option>
              ))}
            </select>
          </label>
        </div>
        {visible.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="line"
              size="sm"
              icon={Icon.Check}
              onClick={() => openBulk("approved")}
              disabled={bulkPending}
            >
              Aprovar visiveis
            </Button>
            <Button
              variant="line"
              size="sm"
              icon={Icon.X}
              onClick={() => openBulk("rejected")}
              disabled={bulkPending}
            >
              Rejeitar visiveis
            </Button>
            <span className="caption">{visible.length} trechos visiveis</span>
          </div>
        )}
      </div>

      <div className="border-y border-[var(--ink)] py-5 mb-8 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="eyebrow">Draft EN</div>
          <p className="caption mt-1">
            {buildReady
              ? "Draft EN gerado com a ultima revisao aprovada."
              : "Gere o draft EN antes de exportar ou avancar para traducao."}
          </p>
          {buildJob.data && (
            <p className="mono text-[11px] text-[var(--ink-3)] mt-1">
              Job {buildJob.data.status} · {buildJob.data.progress}%
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ink"
            size="sm"
            icon={build.isPending || (buildJob.data && !buildJob.isTerminal) ? Icon.Loader : Icon.FileText}
            onClick={startBuild}
            disabled={counts.approved === 0 || build.isPending || (buildJob.data && !buildJob.isTerminal)}
          >
            Gerar draft EN
          </Button>
          {buildReady && hasBuiltSections && (
            <>
              <Button
                variant="line"
                size="sm"
                icon={exportPending === "html-open" ? Icon.Loader : Icon.Eye}
                onClick={() => exportDraft("html", "open")}
                disabled={!!exportPending}
              >
                Ver no navegador
              </Button>
              <Button
                variant="line"
                size="sm"
                icon={exportPending === "docx" ? Icon.Loader : Icon.Download}
                onClick={() => exportDraft("docx", "download")}
                disabled={!!exportPending}
              >
                Baixar Word limpo
              </Button>
              <Button
                variant="line"
                size="sm"
                icon={exportPending === "txt" ? Icon.Loader : Icon.Download}
                onClick={() => exportDraft("txt", "download")}
                disabled={!!exportPending}
              >
                Baixar TXT
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={copied ? Icon.Check : Icon.Copy}
                onClick={copyMonographText}
              >
                Copiar texto
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="caption flex items-center gap-3 mb-8">
          <Spinner /> Carregando revisao...
        </div>
      )}
      {hasError && (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Revisao indisponivel">
          Nao foi possivel carregar os trechos. Recarregue a pagina ou tente novamente.
        </Callout>
      )}
      {reviewDirty && hasBuiltSections && (
        <Callout tone="rule" icon={Icon.RefreshCw} title="Draft EN desatualizado">
          Existem alteracoes aprovadas depois do ultimo build. Gere o draft EN novamente.
        </Callout>
      )}
      {buildJob.isFailed && (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Falha ao gerar draft EN">
          {buildJob.data?.error_message || "Revise as citacoes dos trechos aprovados e tente novamente."}
        </Callout>
      )}

      {!isLoading && !hasError && visible.length === 0 ? (
        <EmptyState
          icon={Icon.CheckCircle}
          title="Nada para revisar"
          description="Todos os trechos deste filtro foram tratados."
        />
      ) : (
        <div className="anim-stagger">
          {visible.map((extraction) => {
            const label = LABELS_16.find((item) => item.key === extraction.label);
            const text = extraction.text_edited_en || extraction.text_verbatim_en;
            const isEditing = editingId === extraction.id;
            const confidence = extraction.confidence == null
              ? "-"
              : `${(extraction.confidence * 100).toFixed(0)}%`;
            const rowDisabled = bulkPending || patchExtraction.isPending;
            return (
              <article
                key={extraction.id}
                className="grid grid-cols-12 gap-6 py-7 border-b border-[var(--rule-2)]"
              >
                <aside className="col-span-12 md:col-span-3">
                  <div className="eyebrow mb-2">§ {label?.group ?? extraction.label}</div>
                  <h3 className="display-5 mb-3">{label?.title_en ?? extraction.label}</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">{statusStamp(extraction.status)}</div>
                    <div className="caption">
                      Confianca · <span className="mono tabular text-[var(--ink)]">{confidence}</span>
                    </div>
                  </div>
                  <div className="marginalia mt-5 pt-3 border-t border-[var(--rule-2)]">
                    Fonte:<br />
                    <span className="text-[var(--ink-2)]">{extraction.source_title ?? "-"}</span>
                    {extraction.citation_number && (
                      <span className="block mono text-[11px] mt-1 not-italic">
                        Citacao ({extraction.citation_number})
                      </span>
                    )}
                  </div>
                </aside>

                <div className="col-span-12 md:col-span-9">
                  {isEditing ? (
                    <div className="space-y-3">
                      <label className="caption block" htmlFor={`edit-${extraction.id}`}>
                        Texto editado em ingles
                      </label>
                      <Textarea
                        id={`edit-${extraction.id}`}
                        value={editText}
                        onChange={(event) => setEditText(event.target.value)}
                        rows={6}
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} disabled={rowDisabled}>
                          Cancelar
                        </Button>
                        <Button variant="ink" size="sm" icon={Icon.Check} onClick={saveEdit} disabled={rowDisabled}>
                          Salvar e aprovar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="serif text-[16.5px] leading-[1.65] text-[var(--ink)] mb-4">
                        {text}
                        {extraction.citation_number && (
                          <sup className="mono text-[11px] text-[var(--ink-3)] ml-1 tabular">
                            ({extraction.citation_number})
                          </sup>
                        )}
                      </p>
                      {extraction.text_edited_en && (
                        <Callout tone="rule" title="Editado">
                          <span className="caption block mb-2">Original verbatim</span>
                          <span className="serif text-[15px]">{extraction.text_verbatim_en}</span>
                        </Callout>
                      )}
                      {extraction.review_notes && (
                        <p className="caption mt-3">Nota: {extraction.review_notes}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-4">
                        <Button
                          variant={extraction.status === "approved" ? "ink" : "line"}
                          size="sm"
                          icon={Icon.Check}
                          onClick={() => patchStatus(extraction.id, "approved")}
                          disabled={rowDisabled}
                        >
                          Aprovar
                        </Button>
                        <Button
                          variant="line"
                          size="sm"
                          icon={Icon.Edit}
                          onClick={() => startEdit(extraction)}
                          disabled={rowDisabled}
                        >
                          Editar
                        </Button>
                        <Button
                          variant={extraction.status === "rejected" ? "ink" : "line"}
                          size="sm"
                          icon={Icon.X}
                          onClick={() => patchStatus(extraction.id, "rejected")}
                          disabled={rowDisabled}
                        >
                          Rejeitar
                        </Button>
                        <a
                          href={extraction.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-auto caption hover:text-[var(--ink)] flex items-center gap-1.5"
                        >
                          Ver fonte <Icon.ExternalLink size={11} />
                        </a>
                      </div>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <BulkConfirmDialog
        bulk={bulkConfirm}
        isPending={bulkPending}
        onCancel={() => setBulkConfirm(null)}
        onConfirm={confirmBulk}
      />
    </div>
  );
}
